import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	ChevronDownOutline,
	ChevronRightOutline,
	DocumentReportOutline,
	DotsHorizontalOutline,
	HStack,
	rem,
	SmileyFaceOutline,
	Text,
	UserGroupOutline,
	VStack,
} from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useCurrentUserReports } from '../../team-management/hooks/use-current-user-reports';
import { IconAndTextContent } from './icon-and-text-content';
import { UserNavAction } from './user-nav-action';
import { UserNavLink } from './user-nav-link';

type TeamMenuProps = {
	isExpanded: boolean;
	isTeamPanelOpen: boolean;
	onToggleTeamPanel: () => void;
};

export const TeamMenu: FC<TeamMenuProps> = ({ isExpanded, isTeamPanelOpen, onToggleTeamPanel }) => {
	const { t } = useTranslation('navigation');
	const user = useCurrentUser();
	const { data: directReports = [] } = useCurrentUserReports();
	const [isCollapsed, setIsCollapsed] = useState(false);

	const isManager = user.is_manager;

	if (!isManager) {
		return null;
	}

	// When sidebar is collapsed, show only the menu items without the collapsible header
	const showCollapsed = isExpanded && isCollapsed;

	return (
		<Box mt={rem(8)}>
			{isExpanded ? (
				<Box
					as="button"
					onClick={() => setIsCollapsed(!isCollapsed)}
					bg="transparent"
					border="none"
					cursor="pointer"
					p={0}
					my={rem(4)}
					w="100%"
					textAlign="left"
					_hover={{ '& .chevron-icon': { opacity: 1 } }}
					_focusVisible={{ outline: '2px solid', outlineColor: 'blue.500', borderRadius: 'sm', outlineOffset: '-2px' }}
					aria-expanded={!isCollapsed}
					aria-controls="team-menu-items"
				>
					<HStack spacing={rem(4)} align="center" justify="flex-start">
						<Text fontSize={50} color="text.softest" fontWeight="medium" pl={rem(10)}>
							{t('teamMenu.title')}
						</Text>
						<Box
							className="chevron-icon"
							opacity={isCollapsed ? 1 : 0}
							transition="opacity 0.15s ease-in-out"
							color="text.softest"
							display="flex"
							alignItems="center"
						>
							{isCollapsed ? <ChevronRightOutline size={3} /> : <ChevronDownOutline size={3} />}
						</Box>
					</HStack>
				</Box>
			) : (
				<Text fontSize={50} color="text.softest" fontWeight="medium" pl={rem(2)} my={rem(4)}>
					{t('teamMenu.title')}
				</Text>
			)}
			<VStack id="team-menu-items" spacing={rem(8)} display={showCollapsed ? 'none' : 'flex'}>
				{/* YOUR TEAM */}
				<UserNavLink to="/team/your-team" isExpanded={isExpanded} data-testid="nav-link-view-profile">
					<IconAndTextContent icon={<UserGroupOutline size="md" />} label={t('teamMenu.yourTeam')} isExpanded={isExpanded} />
				</UserNavLink>

				{/* PULSE RESULTS */}
				<UserNavLink to="/team/pulse-results" isExpanded={isExpanded} data-testid="nav-link-pulse-results">
					<IconAndTextContent icon={<SmileyFaceOutline size="md" />} label={t('teamMenu.pulseResults')} isExpanded={isExpanded} />
				</UserNavLink>

				{/* TEAM REPORT */}
				<UserNavLink to="/team/team-report" isExpanded={isExpanded} data-testid="nav-link-team-report">
					<IconAndTextContent icon={<DocumentReportOutline size="md" />} label={t('teamMenu.teamReport')} isExpanded={isExpanded} />
				</UserNavLink>

				{/* VIEW TEAM */}
				{directReports.length > 0 && (
					<UserNavAction onClick={onToggleTeamPanel} isExpanded={isExpanded} isActive={isTeamPanelOpen} data-testid="nav-link-view-team">
						<IconAndTextContent icon={<DotsHorizontalOutline size="md" />} label={t('teamMenu.viewTeam')} isExpanded={isExpanded} />
					</UserNavAction>
				)}
			</VStack>
		</Box>
	);
};
